import {gaConfig} from '@stencil/hotels-config';
// SELECTORS
import {
  latLongSelectorFn,
  pointOfInterestsDataSelector,
} from '@stencil/hotels-details-reducer/src/selectors';
import detailsPageClickGaPush from '@stencil/hotels-utils/src/details-page-click-ga-push';
import {detailsNavigationIds} from '@stencil/hotels-utils/src/get-details-navigation-id-and-values';
import React, {useCallback, useReducer, useRef} from 'react';
// HOOKS
import {useDispatch, useSelector} from 'react-redux';
import {createSelector} from 'reselect';
import CarIcon from '../../../../../stories/Icons/CarIcon';
import CloseCircleFillIcon from '../../../../../stories/Icons/CloseCircleFillIcon';
import MapLocationIcon from '../../../../../stories/Icons/MapLocationIcon';
// ACTIONS
import {
  showAllNearbyPlaces,
  // updateShowAllPlaces,
  showOnlyUserSelectedLocations,
  updateSelectedPlaces,
} from '../../../HotelsMap/redux/actions';
import {selectedPlacesSelector} from '../../../HotelsMap/redux/selectors';
// TYPES
import {GoogleMapLocationType} from '../../../HotelsMap/redux/types';
// COMPONENTS
import GoogleMap from './GoogleMap';
import {
  PrimaryLink,
  CenteredSpaceWrap,
  SectionHeader,
} from '../../../../../stories/components/CommonStyleUtils/dwebCommon.styles';
import * as S from './LocationMap.styles';
// ICONS

import SomethingWentWrongIcon from '../../../../../../../../dstories/something-went-wrong/src/SomethingWentWrongIcon';

const {DetailsGAClickEventActions} = gaConfig;

const latLongSelector = createSelector([latLongSelectorFn], latLong => latLong);

enum LocationMapStateActionTypes {
  updateUserSearchedLocations = 'updateUserSearchedLocations',
  removeUserSearchedLocations = 'removeUserSearchedLocations',
  updateError = 'updateError',
}

const reducer = (state, action) => {
  switch (action.type) {
    case LocationMapStateActionTypes.updateUserSearchedLocations:
      let localStateCopy = {...state};

      let normalisedIncomingPlaces = action.payload?.userSearchedLocations;

      if (!Array.isArray(action.payload?.userSearchedLocations)) {
        normalisedIncomingPlaces = [action.payload?.userSearchedLocations];
      }

      if (localStateCopy.userSearchedLocations.length > 3) {
        localStateCopy.userSearchedLocations = localStateCopy.userSearchedLocations.slice(1);
      }

      const incomingPlaceAlreadyPresent = normalisedIncomingPlaces.some(place => {
        return localStateCopy.userSearchedLocations.some(prevPlace => prevPlace._id === place._id);
      });

      return {
        ...state,
        userSearchedLocations: [
          ...(!incomingPlaceAlreadyPresent ? localStateCopy.userSearchedLocations : []),
          ...normalisedIncomingPlaces,
        ],
      };

    case LocationMapStateActionTypes.removeUserSearchedLocations:
      return {
        ...state,
        userSearchedLocations: state.userSearchedLocations.filter(
          place => place._id !== action.payload?.placeId,
        ),
      };

    case LocationMapStateActionTypes.updateError:
      return {
        ...state,
        error: action.payload?.error,
      };

    default:
      throw new Error(`${action.type} is not supported in LocationMap state reducer`);
  }
};

const initState = {
  userSearchedLocations: [],
  error: {},
};

const LocationMap = () => {
  const [state, stateDispatch] = useReducer(reducer, initState);

  const updateUserSearchedLocations = useCallback(
    locations => {
      stateDispatch({
        type: LocationMapStateActionTypes.updateUserSearchedLocations,
        payload: {
          userSearchedLocations: locations,
        },
      });
    },
    [stateDispatch],
  );

  const removeUserSearchedLocation = useCallback(
    place => {
      stateDispatch({
        type: LocationMapStateActionTypes.removeUserSearchedLocations,
        payload: {
          placeId: place,
        },
      });
    },
    [stateDispatch],
  );

  // const locationMapImageSrc = useSelector(locationMapImageSelector);
  const {la, lo} = useSelector(latLongSelector);
  const pointOfInterestData = useSelector(pointOfInterestsDataSelector);

  const selectedPlaces = useSelector(selectedPlacesSelector);
  // const showAllPlaces = useSelector(showAllPlacesSelector);

  const pointOfInterestPlacesData = pointOfInterestData.filter(d => d.title === 'poi');
  const transitPointPlacesData = pointOfInterestData.filter(d => d.title === 'transit_point');
  const othersPlacesData = pointOfInterestData.filter(d => d.title === 'others');

  const pointOfInterestsList = pointOfInterestPlacesData?.[0]?.data || [];
  const transitPointPlacesList = transitPointPlacesData?.[0]?.data || [];
  const othersPlaceList = othersPlacesData?.[0]?.data || [];
  const nearbyAttractionsList = pointOfInterestsList.concat(othersPlaceList);

  const identifiableNearbyAttractionList = nearbyAttractionsList.map(item => ({
    ...item,
    type: GoogleMapLocationType.attraction,
  }));

  const identifiableTransitPlacesList = transitPointPlacesList.map(item => ({
    ...item,
    type: GoogleMapLocationType.transit,
  }));

  const allNearbyLocationPlaces = identifiableNearbyAttractionList.concat(
    identifiableTransitPlacesList,
  );

  const dispatch = useDispatch();

  const setSelectedPlaces = places => dispatch(updateSelectedPlaces(places));
  const showHotel = () => dispatch(showOnlyUserSelectedLocations());
  // const setShowAllPlaces = toggle => dispatch(updateShowAllPlaces(toggle));

  const getPlaceEventPropsForMapInteraction = ({place, typeOfPlace, error = {}}) => {
    const updatedPlace = {...place, type: typeOfPlace};

    let eventLabel = '';

    if (typeOfPlace === GoogleMapLocationType.attraction) {
      eventLabel = GoogleMapLocationType.attraction;
    }

    if (typeOfPlace === GoogleMapLocationType.transit) {
      eventLabel = GoogleMapLocationType.transit;
    }

    return {
      // onMouseEnter() {
      //   selectPlace(updatedPlace)
      // },
      // onMouseLeave() {
      //   resetSelectedPlace()
      // },
      onClick() {
        detailsPageClickGaPush({
          eventAction: DetailsGAClickEventActions.locationMap.nearbyPlacesClick,
          eventLabel,
        });

        if (error.message) {
          return;
        }

        setSelectedPlaces(updatedPlace);
      },
    };
  };

  const locationAutosuggestRef = useRef(null);
  const mapContainerRef = useRef(null);

  const thereAreSelectedPlacesOnMapWhichAreNotUserSelected = !!selectedPlaces.filter(
    place => place.type !== GoogleMapLocationType.userSelected,
  ).length;
  const anyNearbyLocationIsNotSelectedOnMap = !selectedPlaces.some(
    place =>
      place.type === GoogleMapLocationType.attraction ||
      place.type === GoogleMapLocationType.transit,
  );

  const showAllNearbyPlacesShouldBeVisible = anyNearbyLocationIsNotSelectedOnMap;
  const showHotelShouldBeVisible = thereAreSelectedPlacesOnMapWhichAreNotUserSelected;
  const thereArePlacesToShow = !!nearbyAttractionsList.length || !!transitPointPlacesList.length;
  const thereIsError = state.error?.message;
  const thereIsNoError = !thereIsError;

  return (
    <S.LocationMapContainer id={detailsNavigationIds.location}>
      <S.LocationMapHeader>
        <SectionHeader>Location</SectionHeader>
      </S.LocationMapHeader>

      {thereIsNoError && (
        <S.SearchNearByLocation>
          <S.SearchIconWrap></S.SearchIconWrap>
          <S.LocationSearchInput
            placeholder="Find nearby attractions and other places near Hotel"
            ref={locationAutosuggestRef}
          />
        </S.SearchNearByLocation>
      )}

      <S.ContentContainer>
        {showAllNearbyPlacesShouldBeVisible && thereIsNoError && (
          <S.MapActionButtonContainer>
            <S.MapActionButton
              onClick={() => {
                detailsPageClickGaPush({
                  eventAction: DetailsGAClickEventActions.locationMap.actionButton,
                  eventLabel: 'SHOW_ALL_PLACES',
                });

                dispatch(showAllNearbyPlaces(allNearbyLocationPlaces));
                // setShowAllPlaces(true);
              }}
            >
              Show All Nearby Places
            </S.MapActionButton>
          </S.MapActionButtonContainer>
        )}

        {showHotelShouldBeVisible && thereIsNoError && (
          <S.MapActionButtonContainer>
            <S.MapActionButton
              onClick={() => {
                detailsPageClickGaPush({
                  eventAction: DetailsGAClickEventActions.locationMap.actionButton,
                  eventLabel: 'SHOW_ONLY_HOTEL',
                });

                showHotel();
              }}
            >
              Show Hotel
            </S.MapActionButton>
          </S.MapActionButtonContainer>
        )}
        <S.MapContainer flex={thereArePlacesToShow ? 0.7 : 1}>
          {thereIsError && <SomethingWentWrongIcon />}

          {thereIsNoError && (
            <GoogleMap
              lat={la}
              lng={lo}
              allNearbyLocationPlaces={allNearbyLocationPlaces}
              selectedPlaces={selectedPlaces}
              showNearbyPlaces={!showAllNearbyPlacesShouldBeVisible}
              refs={{
                mapContainerRef,
                locationAutosuggestRef,
              }}
              updateUserSearchedLocations={updateUserSearchedLocations}
              userSearchedLocations={state.userSearchedLocations}
              onAPILoadError={() => {
                stateDispatch({
                  type: LocationMapStateActionTypes.updateError,
                  payload: {
                    error: {
                      message: 'Google API Call Failed',
                    },
                  },
                });
              }}
              onAPILoadSuccess={() => {
                stateDispatch({
                  type: LocationMapStateActionTypes.updateError,
                  payload: {
                    error: {},
                  },
                });
              }}
            />
          )}

          {thereIsError && (
            <div>
              <S.SomethingWentWrongText>
                Something went wrong! Reload to view Maps
              </S.SomethingWentWrongText>
              <S.ReloadBtn
                onClick={() => {
                  if (window) {
                    window.location.reload();
                  }
                }}
              >
                Reload Now
              </S.ReloadBtn>
            </div>
          )}
        </S.MapContainer>

        {thereArePlacesToShow && (
          <S.WhatIsAroundContainer>
            <S.WhatIsAroundContainerHeader>What's Around?</S.WhatIsAroundContainerHeader>

            <S.PlacesListContainer>
              <S.ListBoxWrapper>
                {!!nearbyAttractionsList.length && (
                  <S.ListHeading>Nearby Attractions (A)</S.ListHeading>
                )}

                {!!nearbyAttractionsList.length && (
                  <S.List>
                    {nearbyAttractionsList.map(nearbyAttraction => {
                      return (
                        <S.ListItem
                          key={nearbyAttraction.n}
                          {...getPlaceEventPropsForMapInteraction({
                            place: nearbyAttraction,
                            typeOfPlace: GoogleMapLocationType.attraction,
                            error: state.error,
                          })}
                        >
                          <S.ListItemWrapper>
                            <S.GreenRound
                              active={
                                !!selectedPlaces.length &&
                                !!selectedPlaces.some(
                                  place => place.type === GoogleMapLocationType.attraction,
                                ) &&
                                selectedPlaces.some(place => place._id === nearbyAttraction._id)
                              }
                            />
                            <S.ListItemTextsContainer>
                              <PrimaryLink>{nearbyAttraction.n}</PrimaryLink>

                              {nearbyAttraction.dist_display && nearbyAttraction.unit_display && (
                                <S.DistanceText>
                                  {nearbyAttraction.dist_display} {nearbyAttraction.unit_display}
                                </S.DistanceText>
                              )}
                            </S.ListItemTextsContainer>
                          </S.ListItemWrapper>
                        </S.ListItem>
                      );
                    })}
                  </S.List>
                )}

                {!!transitPointPlacesList.length && (
                  <S.ListHeading>Nearby Transit (T)</S.ListHeading>
                )}

                {!!transitPointPlacesList.length && (
                  <S.List>
                    {transitPointPlacesList.map(transitDataForPlace => {
                      return (
                        <S.ListItem
                          key={transitDataForPlace.n}
                          {...getPlaceEventPropsForMapInteraction({
                            place: transitDataForPlace,
                            typeOfPlace: GoogleMapLocationType.transit,
                            error: state.error,
                          })}
                        >
                          <S.ListItemWrapper>
                            <S.GreenRound
                              active={
                                !!selectedPlaces.length &&
                                !!selectedPlaces.some(
                                  place => place.type === GoogleMapLocationType.transit,
                                ) &&
                                selectedPlaces.some(place => place._id === transitDataForPlace._id)
                              }
                            />
                            <S.ListItemTextsContainer>
                              <PrimaryLink>{transitDataForPlace.n}</PrimaryLink>
                              {transitDataForPlace.dist_display &&
                                transitDataForPlace.unit_display && (
                                  <S.DistanceText>
                                    {transitDataForPlace.dist_display}{' '}
                                    {transitDataForPlace.unit_display}
                                  </S.DistanceText>
                                )}
                            </S.ListItemTextsContainer>
                          </S.ListItemWrapper>
                        </S.ListItem>
                      );
                    })}
                  </S.List>
                )}
              </S.ListBoxWrapper>
            </S.PlacesListContainer>
          </S.WhatIsAroundContainer>
        )}
      </S.ContentContainer>
      <S.AutoSuggestOuterWrapper>
        {state.userSearchedLocations.map(location => {
          return (
            <S.AutoSuggestContainer>
              <CenteredSpaceWrap>
                <S.LocationMapWrapper>
                  <MapLocationIcon />
                  {location.n && (
                    <S.LocationName>
                      {location.n}
                      <S.LocationNameToolTip>{location.n}</S.LocationNameToolTip>
                    </S.LocationName>
                  )}
                </S.LocationMapWrapper>
                <CloseCircleFillIcon
                  onClick={() => {
                    removeUserSearchedLocation(location._id);
                  }}
                />
              </CenteredSpaceWrap>
              <S.TimeDistanceDurationWrapper>
                {location.timeFromHotel && (
                  <S.TimeDuration>{location.timeFromHotel} |</S.TimeDuration>
                )}
                {location.distanceFromHotel && (
                  <S.DistanceDuration>{location.distanceFromHotel}</S.DistanceDuration>
                )}
                <CarIcon />
              </S.TimeDistanceDurationWrapper>
            </S.AutoSuggestContainer>
          );
        })}
      </S.AutoSuggestOuterWrapper>
    </S.LocationMapContainer>
  );
};

export default LocationMap;
